import React, { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  Globe, Lock, MessageCircle,
  Share2, ChevronLeft, ChevronRight, Loader, Trash2, MoreVertical
} from 'lucide-react';
import { btn } from '../../utils/buttonStyles';

const REACTIONS = [
  { type: 'like',    emoji: '👍', label: 'Like',    color: 'text-blue-500' },
  { type: 'love',    emoji: '❤️', label: 'Love',    color: 'text-red-500' },
  { type: 'haha',    emoji: '😂', label: 'Haha',    color: 'text-yellow-500' },
  { type: 'sad',     emoji: '😢', label: 'Sad',     color: 'text-yellow-500' },
  { type: 'dislike', emoji: '👎', label: 'Dislike', color: 'text-gray-500' },
];
const REACTION_MAP = Object.fromEntries(REACTIONS.map(r => [r.type, r]));
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePostComments } from '../hooks/useSocialFeedRest';
import CommentWithReplies from './CommentWithReplies';
import ReactorsModal from './ReactorsModal';
import ShareModal from '../../components/common/ShareModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';

const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mpeg', '.mpg'];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some(ext => lowerUrl.includes(ext));
};

const formatTime = (dateString) => {
  if (!dateString) return '';
  try {
    const now = new Date();
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const diffMs = now - date;
    const minutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 30) return `${days}d ago`;
    return date.toLocaleDateString();
  } catch {
    return '';
  }
};

/**
 * PostedCard — Display card for an existing post.
 *
 * Props:
 * - post           — post data object (required)
 * - onReact(postId, type) — called when user reacts (required for optimistic update)
 * - onDelete(postId) — called when user deletes post (optional)
 * - currentUserId  — string, used to determine if current user is author (optional)
 * - onShare(postId) — called when share is clicked (optional, shows internal modal if omitted)
 */
const PostedCard = ({ post, onReact, onDelete, currentUserId, isAdmin = false, onShare }) => {
  const { theme } = useTheme();
  const { user: currentUser } = useAuth();
  const { videoDisplay: FEATURE_VIDEO_DISPLAY } = useFeatureFlags();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [reactorsModal, setReactorsModal] = useState(null); // { tab }
  const hoverTimer = useRef(null);
  const pickerRef = useRef(null);

  const { comments, loading: commentsLoading, addComment } = usePostComments(showComments ? post.id : null);

  const isAuthor = currentUserId && post.user?.id && String(post.user.id) === String(currentUserId);

  const handlePrevImage = () => {
    setCurrentImageIndex(prev => prev === 0 ? post.media.length - 1 : prev - 1);
  };

  const handleNextImage = () => {
    setCurrentImageIndex(prev => prev === post.media.length - 1 ? 0 : prev + 1);
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    const result = await addComment(newComment.trim());
    if (result.success) setNewComment('');
  };

  const handleReact = (type) => {
    setShowReactionPicker(false);
    if (onReact) onReact(post.id, type);
  };

  const handleReactionButtonClick = () => {
    // Quick click: toggle current reaction or default to 'like'
    const current = post.userReaction;
    handleReact(current || 'like');
  };

  const handleReactionMouseEnter = () => {
    hoverTimer.current = setTimeout(() => setShowReactionPicker(true), 400);
  };

  const handleReactionMouseLeave = () => {
    clearTimeout(hoverTimer.current);
    // Small delay so user can move mouse into the picker
    hoverTimer.current = setTimeout(() => setShowReactionPicker(false), 300);
  };

  const handlePickerMouseEnter = () => {
    clearTimeout(hoverTimer.current);
  };

  const handlePickerMouseLeave = () => {
    hoverTimer.current = setTimeout(() => setShowReactionPicker(false), 300);
  };

  const currentReaction = post.userReaction ? REACTION_MAP[post.userReaction] : null;

  const handleDelete = () => {
    if (!onDelete || isDeleting) return;
    setShowOptions(false);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setShowDeleteConfirm(false);
    setIsDeleting(true);
    try {
      await onDelete(post.id);
      toast.success('Post deleted');
    } catch {
      toast.error('Failed to delete post');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleShareClick = () => {
    if (onShare) {
      onShare(post.id);
    } else {
      setShowShareModal(true);
    }
  };

  return (
    <div className={`${theme.card} rounded-lg ${theme.shadow} overflow-hidden`}>
      {/* Post Header */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-x-3">
          {post.user?.avatar ? (
            <img
              src={resolveImageUrl(post.user.avatar)}
              alt={post.user.username}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full flex items-center justify-center">
              <span className="text-lg font-bold text-white">
                {post.user?.username?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
          )}
          <div>
            <p className={`font-semibold ${theme.text}`}>{post.user?.username || 'Unknown User'}</p>
            <div className="flex items-center gap-x-2">
              <p className={`text-sm ${theme.textMuted}`}>{formatTime(post.createdAt)}</p>
              <div className="flex items-center gap-x-1">
                {post.privacy === 'public' ? (
                  <Globe className="w-3 h-3 text-gray-500" />
                ) : (
                  <Lock className="w-3 h-3 text-gray-500" />
                )}
                <span className={`text-xs ${theme.textMuted} capitalize`}>
                  {post.privacy || 'public'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Options menu for author or group admin */}
        {(isAuthor || isAdmin) && onDelete && (
          <div className="relative">
            <button
              onClick={() => setShowOptions(!showOptions)}
              className={`p-2 rounded-full ${theme.cardHover} transition-colors`}
              title="Post options"
              aria-label="Post options"
            >
              <MoreVertical className={`w-4 h-4 ${theme.textMuted}`} />
            </button>
            {showOptions && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowOptions(false)} />
                <div className={`absolute right-0 top-8 z-20 ${theme.card} rounded-lg shadow-lg border ${theme.border} min-w-32`}>
                  <button
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="flex items-center gap-x-2 px-4 py-2 w-full text-left text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="text-sm">{isDeleting ? 'Deleting...' : 'Delete'}</span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Post Content */}
      {post.content && (
        <div className="px-4 pb-3">
          <p className={`${theme.text} leading-relaxed whitespace-pre-wrap`}>{post.content}</p>
        </div>
      )}

      {/* Post Media - Carousel */}
      {post.media && post.media.length > 0 && (
        <div className="relative bg-black" style={{ minHeight: '200px' }}>
          {(() => {
            const currentMedia = post.media[currentImageIndex];
            if (!currentMedia || !currentMedia.url) return null;
            if (FEATURE_VIDEO_DISPLAY && isVideoUrl(currentMedia.url)) {
              return <video src={resolveImageUrl(currentMedia.url)} className="w-full max-h-96 object-contain" controls />;
            }
            if (!FEATURE_VIDEO_DISPLAY && isVideoUrl(currentMedia.url)) return null;
            return (
              <img
                src={resolveImageUrl(currentMedia.url)}
                alt={`Media ${currentImageIndex + 1}`}
                className="w-full max-h-96 object-contain"
                style={{ display: 'block', minHeight: '100px' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            );
          })()}

          {post.media.length > 1 && (
            <>
              <button
                onClick={handlePrevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/75 text-white rounded-full p-2 transition-all"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={handleNextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/75 text-white rounded-full p-2 transition-all"
                aria-label="Next image"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/75 text-white px-3 py-1 rounded-full text-sm">
                {currentImageIndex + 1} / {post.media.length}
              </div>
              <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex gap-x-2">
                {post.media.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${index === currentImageIndex ? 'bg-white w-6' : 'bg-white/50'}`}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Engagement Stats */}
      {(() => {
        const counts = post.reactionCounts || {};
        const activeReactions = REACTIONS.filter(r => (counts[r.type] || 0) > 0)
          .sort((a, b) => (counts[b.type] || 0) - (counts[a.type] || 0));
        const totalReactions = activeReactions.reduce((sum, r) => sum + (counts[r.type] || 0), 0);
        return (totalReactions > 0 || post.commentCount > 0) ? (
          <div className={`px-4 py-2 border-b ${theme.border}`}>
            <div className="flex justify-between items-center text-sm">
              {/* Per-type reaction counts — click anywhere opens who-reacted modal */}
              <div className="flex items-center gap-1.5 flex-wrap">
                {activeReactions.map(r => (
                  <button
                    key={r.type}
                    onClick={() => setReactorsModal({ tab: r.type })}
                    title={`See who reacted with ${r.label}`}
                    className={`flex items-center gap-x-1 px-1.5 py-0.5 rounded-full text-xs border transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      post.userReaction === r.type
                        ? `${r.color} border-current bg-blue-50 dark:bg-blue-900/20`
                        : `${theme.textMuted} border-gray-200 dark:border-gray-600`
                    }`}
                  >
                    <span className="text-sm leading-none">{r.emoji}</span>
                    <span className="font-medium">{counts[r.type]}</span>
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-x-4">
                {post.commentCount > 0 && (
                  <span
                    className={`${theme.textSecondary} hover:underline cursor-pointer font-medium`}
                    onClick={() => setShowComments(!showComments)}
                  >
                    {post.commentCount} {post.commentCount === 1 ? 'comment' : 'comments'}
                  </span>
                )}
              </div>
            </div>
          </div>
        ) : null;
      })()}

      {/* Action Buttons */}
      <div className={`px-2 py-2 border-b ${theme.border}`}>
        <div className="flex gap-x-1 justify-around">
          {/* Reaction button with hover picker */}
          <div className="relative flex-shrink-0">
            <button
              onClick={handleReactionButtonClick}
              onMouseEnter={handleReactionMouseEnter}
              onMouseLeave={handleReactionMouseLeave}
              className={`flex items-center gap-x-1 px-3 py-2 rounded-lg transition-colors justify-center ${
                currentReaction ? currentReaction.color : `${theme.textMuted} hover:text-blue-500`
              } ${theme.cardHover}`}
              title={currentReaction ? `Remove ${currentReaction.label}` : 'Like'}
            >
              <span className="text-lg leading-none">
                {currentReaction ? currentReaction.emoji : '👍'}
              </span>
              <span className={`font-medium text-xs sm:text-sm ${currentReaction ? '' : ''}`}>
                {currentReaction ? currentReaction.label : 'Like'}
              </span>
            </button>

            {/* Reaction Picker */}
            {showReactionPicker && (
              <div
                ref={pickerRef}
                onMouseEnter={handlePickerMouseEnter}
                onMouseLeave={handlePickerMouseLeave}
                className={`absolute bottom-12 left-0 z-30 ${theme.card} rounded-full shadow-xl border ${theme.border} px-2 py-1.5 flex gap-x-1`}
              >
                {REACTIONS.map(r => (
                  <button
                    key={r.type}
                    onClick={() => handleReact(r.type)}
                    title={r.label}
                    className={`flex flex-col items-center p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all hover:scale-125 ${
                      post.userReaction === r.type ? 'scale-110 bg-gray-100 dark:bg-gray-700' : ''
                    }`}
                  >
                    <span className="text-xl leading-none">{r.emoji}</span>
                    <span className={`text-xs mt-0.5 font-medium ${r.color}`}>{r.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => setShowComments(!showComments)}
            className={`flex items-center gap-x-1 px-3 py-2 rounded-lg transition-colors justify-center flex-shrink-0 ${theme.textMuted} hover:${theme.text} ${theme.cardHover}`}
          >
            <MessageCircle className="w-5 h-5" />
            <span className="font-medium text-xs sm:text-sm">Comment</span>
          </button>

          <button
            onClick={handleShareClick}
            className={`flex items-center gap-x-1 px-3 py-2 rounded-lg transition-colors justify-center flex-shrink-0 ${theme.textMuted} hover:${theme.text} ${theme.cardHover}`}
          >
            <Share2 className="w-5 h-5" />
            <span className="font-medium text-xs sm:text-sm">Share</span>
          </button>
        </div>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className={`${theme.backgroundSecondary} border-t ${theme.border} flex flex-col`} style={{ maxHeight: '320px' }}>
          <div
            className="p-4 space-y-3 overflow-y-auto flex-1 min-h-0"
            style={{ overscrollBehavior: 'contain' }}
          >
            {commentsLoading ? (
              <div className="flex justify-center py-4">
                <Loader className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : comments.length > 0 ? (
              comments.map(comment => (
                <CommentWithReplies
                  key={comment.id}
                  comment={comment}
                  formatTime={formatTime}
                  postId={post.id}
                />
              ))
            ) : (
              <div className="text-center py-4">
                <MessageCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className={`text-sm ${theme.textMuted}`}>No comments yet. Be the first to comment!</p>
              </div>
            )}
          </div>

          <div className={`p-4 border-t ${theme.border} flex-shrink-0`}>
            <form onSubmit={handleSubmitComment} className="flex gap-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-white">
                  {currentUser?.firstName?.charAt(0)?.toUpperCase() || currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 flex gap-2">
                <input
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  className={`flex-1 px-4 py-2 rounded-full ${theme.background} border ${theme.border} focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.text} placeholder-gray-500`}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmitComment(e);
                    }
                  }}
                />
                <button
                  type="submit"
                  disabled={!newComment.trim()}
                  className={`${btn.base} ${btn.sm} ${theme.buttonPrimary}`}
                >
                  Post
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        entity={{ id: post.id, name: post.content?.slice(0, 60) || 'Post' }}
        entityType="post"
        urlPattern="/social/posts/{id}"
        currentUser={currentUser}
      />

      {/* Reactors Modal */}
      {reactorsModal && (
        <ReactorsModal
          endpoint={`/posts/${post.id}/reactors`}
          counts={post.reactionCounts || {}}
          initialTab={reactorsModal.tab}
          onClose={() => setReactorsModal(null)}
        />
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete this post?"
        message="This action cannot be undone."
        confirmLabel="Delete"
        confirmDanger
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};

export default PostedCard;
